import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageBanner from "@/components/base/PageBanner";
import JsonLd from "@/components/base/JsonLd";
import MangaCard from "@/components/feature/MangaCard";
import { categories, mangaSeries } from "@/mocks/manga";

export default function SeriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "全部";
  const keyword = searchParams.get("search") || "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const list = useMemo(() => {
    return mangaSeries.filter((m) => {
      const matchCat = category === "全部" || m.category === category;
      const matchKw =
        !keyword ||
        m.title.includes(keyword) ||
        m.author.includes(keyword) ||
        m.tags.some((t) => t.includes(keyword));
      return matchCat && matchKw;
    });
  }, [category, keyword]);

  const setCategory = (c: string) => {
    const next = new URLSearchParams(searchParams);
    if (c === "全部") next.delete("category");
    else next.set("category", c);
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("search", input.trim());
    else next.delete("search");
    setSearchParams(next, { replace: true });
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://897kg.cc/" },
      { "@type": "ListItem", position: 2, name: "追更书架", item: "https://897kg.cc/series" },
    ],
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "66漫画网 追更书架",
    url: "https://897kg.cc/series",
    inLanguage: "zh-CN",
  };

  return (
    <>
      <JsonLd id="jsonld-breadcrumb-series" data={breadcrumbSchema} />
      <JsonLd id="jsonld-collection" data={collectionSchema} />

      <PageBanner
        eyebrow="追更书架"
        title="全部漫画"
        desc="按分类浏览或搜索你喜欢的连载漫画，一键查看每部漫画的最新进度与更新状态。"
        crumbs={[{ label: "首页", to: "/" }, { label: "追更书架" }]}
      />

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCategory("全部")}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                category === "全部"
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => setCategory(c.name)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  category === c.name
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <form onSubmit={submitSearch} className="flex w-full items-center gap-2 sm:w-auto sm:self-end" role="search">
            <label htmlFor="series-search" className="sr-only">搜索漫画</label>
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400 sm:w-72">
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                id="series-search"
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入漫画名 / 作者 / 标签"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>

        <p className="mt-6 text-sm text-foreground-600">
          当前分类：<span className="font-bold text-primary-600">{category}</span> · 共{" "}
          <span className="font-bold text-primary-600">{list.length}</span> 部作品
          {keyword && <span> · 关键词「{keyword}」</span>}
        </p>

        {list.length > 0 ? (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {list.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-line text-4xl text-foreground-400"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的漫画，换个关键词试试吧。</p>
          </div>
        )}
      </section>
    </>
  );
}